import { Box, Button, Stack, styled, Typography, useTheme } from "@mui/material";
import { HorizontalGaugeChart, FoodsIcon, WhiteBox, FoodsIconButton, FoodsTags } from "@foodpilot/foods";
import i18n from "src/i18n";
import { t } from "i18next";
import { formatNumber } from "src/utils/formatting";
import { EmptyCard } from "./EmptyCard";

export type ComparisonData = {
    key: string;
    title: string;

    min: number | undefined | null;
    max: number | undefined | null;
    value: number | undefined | null;
    average: number | undefined | null;
    count: number | undefined | null;

    action?: () => void;

    noData?: boolean;
    noDimensions?: boolean;
};

type ScoreCardProps = {
    score: ComparisonData | undefined;
    unit: string;
    scorePrecision: number;
};

const minimumComprisonSampleSize: number = 5;

export const ComparisonCard = (props: ScoreCardProps) => {
    const { score, unit, scorePrecision } = props;
    const theme = useTheme();

    if (!score) return null;

    const isCardEmpty = score.noData || score === undefined;
    const isSampleSufficient = !isCardEmpty && !!score.count && score.count >= minimumComprisonSampleSize;
    return (
        <ComparisonCardOuter>
            <ComparisonCardContent>
                <ComparisonHeader>
                    <Typography variant="h4">
                        {score.title}
                        <Typography variant="body" color="grey.900">
                            {unit ? ` - ${unit}` : ""}
                        </Typography>
                    </Typography>
                </ComparisonHeader>
                {!isSampleSufficient ?
                    <EmptyCard />
                :   <Stack gap={theme.spacing(3)} alignItems="flex-start">
                        <HorizontalGaugeChart
                            minValue={score.min ?? 0}
                            maxValue={score.max ?? 9999}
                            companyValue={score.value ?? 0}
                            averageValue={score.average ?? 0}
                            unit={unit}
                            labelCompany={t("score.details.comparison.company")}
                            labelAverage={t("score.details.comparison.average")}
                            backgroundProps={{
                                gradient: {
                                    start: theme.palette.primary[100]!,
                                    end: theme.palette.primary[900]!,
                                },
                            }}
                            valueFormatter={(value) =>
                                formatNumber(value, i18n.resolvedLanguage, {
                                    maximumFractionDigits: scorePrecision,
                                })
                            }
                        />
                        <SampleSizeTag count={score.count ?? 0} />
                    </Stack>
                }
            </ComparisonCardContent>

            {score.action !== undefined && isSampleSufficient && (
                <ComparisonDetailButton
                    onClick={() => {
                        score.action?.();
                    }}
                >
                    {t("score.details.comparison.seeDetails")}
                    <FoodsIcon icon="arrowRight" size={1} />
                </ComparisonDetailButton>
            )}
        </ComparisonCardOuter>
    );
};

type SampleSizeTagProps = {
    count: number;
};

const SampleSizeTag = (props: SampleSizeTagProps) => {
    const { count } = props;

    return <FoodsTags label={t("score.details.comparison.basedOn", { count })} variant="tags-white" disabled />;
};

const ComparisonCardOuter = styled(WhiteBox)(() => ({
    height: "100%",
    width: "100%",
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "column",
}));

const ComparisonCardContent = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
}));

const ComparisonDetailButton = styled(Button)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 0,
    padding: theme.spacing(4),
    width: "100%",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    color: theme.palette.primary[700],
    "&:hover": {
        textDecoration: "none",
        color: theme.palette.primary[900],
    },
}));

const ComparisonHeader = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(4),
    padding: theme.spacing(0, 0.5),
    display: "inline-flex",
    gap: theme.spacing(0.5),
}));
